<template>
<master-layout pageTitle="My Profile">
    <div class="chat-toolbar">
      <div class="toolbar-start">
        <h2 class="vtext-page-title--regular">Edit profile</h2>
      </div>
      <div>
        <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Save" @click="updateUserAccountDetails" :disabled="!addChanged && !pwValidMatch"></VButton>
      </div>
    </div>
    <div class="vgrid--no-padding gridMargin">
      <div class="vrow vmb-20">
        <div class="vcol vcol--no-padding vcol-xl-4 vcol-lg-4 vcol-md-4 vcol-sm-12 vcol-xs-12">
          <div class="model-sections marginBottomRight marginBottom">
            <div class="cust-brdr sectionPadding">
              <h2 class="vtext-card-title--light">Profile details</h2>
            </div>
            <div class="sectionPadding">
              <div class="marginBottom">
                <div class="info-box marginBottom">
                  <VIcon name="info-line" :size="-5" connotation="cta" class="infoIcon"></VIcon>
                  <p class="vtext-body">Recommended size: 500px x 500px. Max file size 4MB (.png, .jpg only) </p>
                </div>
                <div class="flex-center">      
                  <div>
                    <upload-files-v-2
                      type="image" :multiple="false" allowedImageTypes="image/png, image/jpeg, image/jpg" id="profilePicInput" ref="profilePicInput" :maxFileSizeAllowed="4" v-model:loading="profilePictureUploading" @send-upload-data="handleImageUpload"
                      :meta="{is_export: false, is_broadcast: false, is_redact: false, is_public: true, conversation_id: undefined, channel: undefined}">
                      <div v-if="!roleAccountDetails?.image" class="img-block" :class="{'disabled': profilePictureUploading}">
                        <div v-if="profilePictureUploading" class="uploadImgContainer">
                          <VProgressRing class="vvd-spinner" connotation="cta" indeterminate></VProgressRing>
                          <span class="vtext-body">Uploading...</span>
                        </div>
                        <div v-else class="uploadImgContainer">
                          <VIcon name="image-line"></VIcon>
                          <span class="vtext-body">Upload image</span>
                        </div>
                      </div>
                      <div v-else>
                        <div class="cursorPointer">
                          <VProgressRing v-if="profilePictureUploading" class="vvd-spinner uploadImgContainer" connotation="cta" indeterminate></VProgressRing>
                          <img v-else :src="roleAccountDetails.image" alt="profile image" class="uploadedImage" />
                        </div>
                      </div>
                    </upload-files-v-2>
                  </div>
                </div>
              </div>
              <div class="marginBottom">
                <p class="vtext-subtitle vpb-10">Name (Max 30 characters)</p>
                <div class="counter-wrapper">
                  <VTextField v-model="roleAccountDetails.name" :maxlength="30" placeholder="Name" aria-label="name" appearance="ghost" class="vivid-input"></VTextField>
                  <div class="custom-counter">
                      {{ (roleAccountDetails.name || '').length }}/30
                  </div>
                </div>
              </div>
              <div class="marginBottom">
                <div class="flex-start paddingBottom">
                  <p class="vtext-subtitle">Account email</p>
                  <HelpTooltip>The email using which you want to login into your dashboard</HelpTooltip>
                </div>
                <VTextField type="email" readonly v-model="roleAccountDetails.email" @change="validate(roleAccountDetails.email)" placeholder="Login Email" aria-label="account email" appearance="ghost" class="vivid-input"></VTextField>
              </div>
              <div v-if="whatsappConnected">
                <div class="flex-start paddingBottom">
                  <p class="sub-title ion-no-margin">Unique live chat link</p>
                  <HelpTooltip> For example: it will appear as https://shop.{{ domain }}/talktous/{{ user.referallshopName }}/{{ talkToUsHashId }}/wa/&lt;keyword&gt; </HelpTooltip>
                </div>
                <div v-if="roleAccountDetailsClone.agent_nickname" class="liveChatLinksWrapper">
                  <div class="liveChatLinks marginBottom">
                    <div class="channelIcon">
                      <img src="@/resources/images/whatsapp_icon.svg" class="custBorderRadius" alt="whatsapp" />
                    </div>
                    <div class="linkWidth">
                      <p class="headerTitle ion-no-margin">WhatsApp</p>
                      <p v-if="livechatLink" class="vtext-subtitle">{{ livechatLink }}</p>
                      <p v-else class="vtext-subtitle" style="color: var(--gray-04);">Generating link...</p>
                    </div>
                  </div>
                  <div class="liveChatLinks">
                    <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Copy to clipboard" :disabled="!livechatLink" @click="copyToClipboard(livechatLink)"></VButton>
                    <VButton class="vvd-btn-secondary" connotation="cta" appearance="outlined" label="Change Keyword" @click="roleAccountDetailsClone.agent_nickname = ''"></VButton>
                  </div>
                </div>
                <template v-else>
                  <VTextField type="text" v-model="roleAccountDetails.agent_nickname" aria-label="keyword" placeholder="Enter your link keyword" appearance="ghost" class="vivid-input"></VTextField>
                  <div class="flex-between paddingTop">
                    <VButton class="vvd-btn-secondary flex100P" connotation="cta" appearance="outlined" label="Cancel" @click="roleAccountDetailsClone.agent_nickname = roleAccountDetails.agent_nickname = roleDetails.agent_nickname;"></VButton>
                    <VButton class="vvd-btn-primary flex100P" connotation="cta" appearance="filled" label="Save" @click="agent_nicknameFormSubmit"></VButton>
                  </div>
                </template>
              </div>
            </div>
          </div>
          <div class="model-sections containerPadding marginBottom">
            <div class="flexBetween">
              <p class="vtext-card-title--light">Go to business profile</p>
              <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" icon="arrow-right-line" aria-label="Go to business profile" @click="$router.push('/settings/account')"></VButton>
            </div>
          </div>
        </div>
        <div class="vcol vcol--no-padding vcol-xl-8 vcol-lg-8 vcol-md-8 vcol-sm-12 vcol-xs-12 paddingLeft">
          <div v-if="!isLoggedInViaSSO" class="model-sections marginBottom">
            <div class="cust-brdr sectionPadding">
              <h2 class="vtext-card-title--light">Change password</h2>
            </div>
            <div class="sectionPadding">
              <form class="optionsContainer cust-brdr">
                <div class="marginBottom">
                  <p class="vtext-subtitle vpb-10">Current password</p>
                  <div class="passwordField">
                    <VTextField :type="oldPassword ? 'text' : 'password'" v-model="currPwd" aria-label="current password" placeholder="Enter current password" appearance="ghost" class="vivid-input"></VTextField>
                    <VIcon :name="oldPassword ? eyeOutline : eyeOffOutline" @click="togglePasswordVisibility('oldPassword')" class="passwordToggle" />
                  </div>
                </div>
                <div class="marginBottom">
                  <p class="vtext-subtitle vpb-10">New password</p>
                  <div class="passwordField">
                    <VTextField :type="newPassword ? 'text' : 'password'" v-model="newPwd" aria-label="new password" placeholder="Enter new password" appearance="ghost" class="vivid-input"></VTextField>
                    <VIcon :name="newPassword ? eyeOutline : eyeOffOutline" @click="togglePasswordVisibility('newPassword')" class="passwordToggle" />
                  </div>
                </div>
                <div>
                  <p class="vtext-subtitle vpb-10">Confirm new password</p>
                  <div class="passwordField">
                    <VTextField :type="confirmPassword ? 'text' : 'password'" v-model="confirmPwd" aria-label="confirm password" placeholder="Enter new password" appearance="ghost" class="vivid-input"></VTextField>
                    <VIcon :name="confirmPassword ? eyeOutline : eyeOffOutline" @click="togglePasswordVisibility('confirmPassword')" class="passwordToggle" />
                  </div>
                </div>
              </form>

              <div class="passwordConditions">
                <p class="vtext-subtitle vmb-20">Password requirements</p>
                <check-password :password="newPwd" @validated="e => isNewPwValid = e"></check-password>
              </div>
            </div>
          </div>

          <div v-if="!isLoggedInViaSSO" class="model-sections marginBottom">
            <div class="cust-brdr sectionPadding">
              <h2 class="vtext-card-title--light">MFA</h2>
            </div>
            <div class="sectionPadding">
              <div class="info-box marginBottom">
                <VIcon name="info-line" :size="-5" connotation="cta" class="infoIcon"></VIcon>
                <p class="vtext-body">Only one MFA option can be enabled at a time.</p>
              </div>
              <div class="marginBottom innerContainer">
                <div class="flex-between">
                  <div class="authContainer">
                    <VIcon name="envelope-line" :size="0" class="mailIcon"></VIcon>
                    <div>
                      <p class="vtext-card-title vpb-5">Email OTP</p>
                      <p class="vtext-subtitle">Setup email to login with an OTP</p>
                    </div>
                  </div>
                  <VButton v-if="enabledMFA !== 'email'" class="vvd-btn-primary" connotation="cta" appearance="filled" :label="!isLoading ? 'Enable' : 'Loading...'" @click="enable('email')" :disabled="isLoading || enabledMFA === 'email'"></VButton>
                  <VButton v-else class="vvd-btn-success-outline" connotation="success" appearance="outlined" label="Enabled"></VButton>
                </div>
              </div>
              <div class="innerContainer">
                <div class="flex-between">
                  <div class="authContainer">
                    <img src="@/resources/images/google-authenticator-logo.png" width="40" alt="google_auth" />
                    <div>
                      <p class="vtext-card-title vpb-5">Google Authenticator</p>
                      <p class="vtext-subtitle">Setup Google Authenticator to login with a code</p>
                    </div>
                  </div>
                  <VButton v-if="enabledMFA !== 'google'" class="vvd-btn-primary" connotation="cta" appearance="filled" :label="!isLoading ? 'Enable' : 'Loading...'" @click="enable('google')" :disabled="isLoading || enabledMFA === 'google'"></VButton>
                  <VButton v-else class="vvd-btn-success-outline" connotation="success" appearance="outlined" label="Enabled"></VButton>
                </div>  
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- MFA Email Verification -->
    <VDialog :open="enableEmailVerification" headline="Setup Email OTP" modal @close="() => { enableEmailVerification = false; emailOTP = ''; }">
      <template #body>
        <div class="gridMargin">
          <p class="vtext-subtitle vpb-10"> Enter the OTP sent to Email. {{ expiryTimer > 0 ? `Expires in ` + formattedExpiryTimer : '' }} </p>
          <InputNumber name="otp" maxlength="8" @keyup.enter="verifyMFA('email')" v-model="emailOTP" :allowDecimal="false" placeholder="Enter OTP" ariaLabel="otp"> </InputNumber>
          <span v-if="expiryTimer > 0" class="vtext-hint vpt-10">Haven't received the OTP? Please check your spam folder or click on Resend OTP</span>
          <span v-else class="vtext-hint">Your OTP has expired. Please click on Resend OTP</span>
        </div>
      </template>
      <template #action-items>
        <div class="footerBttn">
          <VButton class="vvd-btn-primary flex100P" connotation="cta" appearance="filled" :label="!commonLoader ? 'Verify' : 'Verifying...'" @click="verifyMFA('email')" :disabled="commonLoader || emailOTP == ''"></VButton>
          <VButton class="vvd-btn-ghost flex100P" connotation="cta" appearance="ghost" :label="isResendDisabled ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'" @click="handleResendOtp('email')" :disabled="isResendDisabled"></VButton>
        </div>
      </template>
    </VDialog>

    <!-- MFA Authenticator Verification -->
    <VDialog :open="enableAuthVerification" headline="Setup Authenticator" modal @close="() => { enableAuthVerification = false; authOTP = ''; }">
      <template #body>
        <div class="gridMargin">
          <MfaSteps :qrCode="qrCode" />
          <InputNumber name="otp" :disabled="commonLoader" :maxlength="6" @keyup.enter="verifyMFA('Authenticator')" v-model="authOTP" :allowDecimal="false" :placeholder="`Enter code`" ariaLabel="otp"> </InputNumber>
        </div>
      </template>
      <template #action-items>
        <VButton class="vvd-btn-primary vvd-btn-block" connotation="cta" appearance="filled" :label="!commonLoader ? 'Confirm' : 'Confirming...'" @click="verifyMFA('Authenticator')" :disabled="commonLoader || authOTP == ''"></VButton>
      </template>
    </VDialog>
</master-layout>
</template>

<script>
import HelpTooltip from "/src/components/HelpTooltip.vue";
import MfaSteps from "/src/components/mfa-steps.vue";
import InputNumber from "/src/components/inputNumber.vue";
import moment from "moment";
import apiInterceptor from '@/shared/apiInterceptor';
import { queryClient } from '@/shared/queryClient';
import UploadFilesV2 from '@/components/uploadFilesV2.vue';
import CheckPassword from '@/components/checkPassword.vue';
import { useUIStore } from '@/stores/ui';
import { useAuthStore } from '@/stores/auth';
import { validateEmail } from '@/helpers/validation.helpers';
import { isEuServer } from '@/helpers/env.helpers';
import { useTalkToUsLink } from '@/composables/useTalkToUsLink';
import { environment } from '@/shared/env';
import { useSocialChannels } from '@/composables/useSocialChannels';
import { useRoleDetails } from '@/composables/useRoleDetails';

export default {
  components: { UploadFilesV2, HelpTooltip, MfaSteps, InputNumber, CheckPassword },
  setup() {
    const { whatsappConnected } = useSocialChannels();
    const { roleDetails } = useRoleDetails();
    const { talkToUsBaseUrl } = useTalkToUsLink();
    return { whatsappConnected, roleDetails, talkToUsBaseUrl };
  },
  data() {
    return {
      refreshOutline: 'refresh-line',
      eyeOutline: 'eye-line',
      eyeOffOutline: 'eye-hide-line',
      profilePictureUploading: false,
      roleAccountDetails: {
        email: undefined,
        name: undefined,
        image: undefined,
        dynamics: [],
        agent_nickname: undefined
      },
      oldPassword: false,
      confirmPassword: false,
      newPassword: false,
      currPwd: '',
      newPwd: '',
      confirmPwd: '',
      isNewPwValid: false,
      enabledMFA: undefined,
      roleAccountDetailsClone: {
        email: undefined,
        name: undefined,
        image: undefined,
        dynamics: [],
        agent_nickname: undefined
      },
      enableEmailVerification: false,
      enableAuthVerification: false,
      emailOTP: '',
      authOTP: '',
      commonLoader: false,
      qrCode: undefined,
      expiryTimer: 300, // 5 minutes
      isResendDisabled: true, 
      timerInterval: null,
      resendTimer: 20
    }
  },
  async mounted(){
    try {
      useUIStore().updateLoading({ isLoading: true, message: 'Loading profile...' });
      for(var prop in this.roleAccountDetails) this.roleAccountDetails[prop] = this.roleDetails[prop];
      this.roleAccountDetailsClone = JSON.parse(JSON.stringify(this.roleAccountDetails));
      await this.getMFA();
      useUIStore().updateLoading({ isLoading: false });
    } catch (error) {
      console.error("Exception", error);
      useUIStore().updateLoading({ isLoading: false });
    }
  },
  beforeUnmount() {
    [this.timerInterval, this.resendInterval].forEach(interval => {
      if (interval) clearInterval(interval);
    });
  },
  computed: {
    isLoading() { return useUIStore().isLoading; },
    isLoggedInViaSSO() { return useAuthStore().isLoggedInViaSSO; },
    user() { return useAuthStore().cleanUserData; },
    domain() {
      return isEuServer() ? 'jumper-emea.com' : 'jumper.ai';
    },
    pwValidMatch(){
      return this.newPwd && this.newPwd.length && this.newPwd === this.confirmPwd;
    },
    isDisabled(){
      return !this.currPwd || !this.newPwd || !this.confirmPwd;
    },
    addChanged() {
      for (var prop in this.roleAccountDetails) {
        if (this.roleAccountDetails[prop] !== this.roleAccountDetailsClone[prop] || (this.roleAccountDetails[prop] === '' && this.roleAccountDetailsClone[prop] !== '')) {
          return true;
        }
      }
      return false;
    },
    formattedExpiryTimer(){
      return moment.utc(this.expiryTimer * 1000).format('mm:ss');
    },
    livechatLink() {
      if (!this.talkToUsBaseUrl || !this.roleAccountDetails.agent_nickname) return '';
      return this.talkToUsBaseUrl + '/wa/' + this.roleAccountDetails.agent_nickname;
    },
    talkToUsHashId() {
      // Extracts the hash segment from the base URL for the tooltip.
      // e.g. https://shop.jumperdev.com/talktous/extteam/c676807... → c676807...
      if (!this.talkToUsBaseUrl) return '&lt;hash-id&gt;';
      return this.talkToUsBaseUrl.split('/').pop() || '&lt;hash-id&gt;';
    }
  },
  watch: {
    // Watch for the expiryTimer to become zero and enable resend
    expiryTimer(newValue) {
      if (newValue <= 0) {
        this.isResendDisabled = false;
      }
    },
  },
  methods: {
    async agent_nicknameFormSubmit() {
      try {
        if(!this.roleAccountDetails.agent_nickname || this.roleAccountDetails.agent_nickname.length < 3){
          useUIStore().updateToast({
            open: true,
            message: "Please enter link keyword with at least 3 characters",
            type: "danger"
          })
          return;
        }

        useUIStore().updateLoading({ isLoading: true, message: 'Saving keyword...' });
        let reqBody = {
          agent_nickname: this.roleAccountDetails.agent_nickname
        }
        await apiInterceptor.patch(environment('VUE_APP_API_URL') + "roles/v1/role-details/", reqBody);
        useUIStore().updateToast({
          open: true,
          message: "Link Keyword updated successfully!",
          type: "success"
        })
        this.roleAccountDetailsClone.agent_nickname = this.roleAccountDetails.agent_nickname;
        await queryClient.invalidateQueries({ queryKey: ['account-settings'] });
        await queryClient.invalidateQueries({ queryKey: ['role-details'] });
        await useAuthStore().checkToken();
      } catch (error) {
        console.error("Exception: ", error);
        useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong!",
          type: "danger"
        })
      } finally {
				useUIStore().updateLoading({ isLoading: false });
      }
    },
    copyToClipboard(content) {
			try {
        navigator.clipboard.writeText(content);
        useUIStore().updateToast({
          open: true,
          message: "Copied to the clipboard!",
          type: "success"
        })
      } catch (error) {
        console.error("Exception: ", error);
        useUIStore().updateToast({
          open: true,
          message: "Something went wrong",
          type: "danger"
        })
      }
		},
    async handleImageUpload (urlData) {
      try {
        this.profilePictureUploading = true;
        let reqBody = {
          image: urlData[0].url
        };
        await apiInterceptor.patch(environment('VUE_APP_API_URL') + "roles/v1/role-details/", reqBody);
        useUIStore().updateToast({
          open: true,
          message: "Your profile picture has been updated successfully!",
          type: "success"
        })
        this.roleAccountDetails.image = urlData[0].url;
        await queryClient.invalidateQueries({ queryKey: ['role-details'] });
        await useAuthStore().checkToken();
      } catch (error) {
        console.error("Exception: ", error);
        useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong",
          type: "danger"
        })
      } finally {
        this.profilePictureUploading = false;
      }
    },
    validate(email){
      if (!validateEmail(email)) {
        useUIStore().updateToast({ open: true, message: "Please enter a valid email ID", type: "danger" });
        return;
      }
    },
    togglePasswordVisibility(field) {
      if(field == 'oldPassword') {
        this.oldPassword = !this.oldPassword;
      } else if( field == 'newPassword') {
        this.newPassword = !this.newPassword
      } else if(field == 'confirmPassword'){
        this.confirmPassword = !this.confirmPassword
      }
    },
    async getMFA() {
      try {
        if(this.isLoggedInViaSSO) return;
        useUIStore().updateLoading({ isLoading: true, message: 'Fetching MFA settings...' });
        const mfaResponse = await apiInterceptor.get(environment('VUE_APP_API_URL') + "auth/mfa-setup");
        this.enabledMFA = mfaResponse.type;
      } catch (error) {
        console.error("Exception", error);
        useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong",
          type: "danger"
        })
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },
    async enable(type) {
      try {
				useUIStore().updateLoading({ isLoading: true, message: 'Setting up MFA...' });
				let reqBody = {
					type: type
				};
				const response = await apiInterceptor.post(environment('VUE_APP_API_URL') + "auth/mfa-setup/edit/request", reqBody);
        if(type == 'email'){
          this.enableEmailVerification = true;
          useUIStore().updateToast({ open: true, message: "OTP has been sent to your email", type: "success" });
          this.startTimer();
          this.startResendTimer();
        } else {
          this.qrCode = response.qr_code;
          this.enableAuthVerification = true;
        }
			} catch (error) {
				console.error("Exception", error);
				useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong",
          type: "danger"
        })
			} finally {
				useUIStore().updateLoading({ isLoading: false });
			}
    },
    async verifyMFA(type) {
      try {
				this.commonLoader = true;
				let reqBody = {
					code: type == 'email' ? this.emailOTP : this.authOTP
				};
				await apiInterceptor.post(environment('VUE_APP_API_URL') + "auth/mfa-setup/edit/verify", reqBody);
        this.enabledMFA = type;
        useUIStore().updateToast({
          open: true,
          message: "MFA setting is updated!",
          type: "success"
        });
        this.authOTP = '';
        this.emailOTP = '';
        this.enableEmailVerification = false;
        this.enableAuthVerification = false;
        await this.getMFA();
			} catch (error) {
				console.error("Exception", error);
				useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong",
          type: "danger"
        })
			} finally {
				this.commonLoader = false;
			}
    },
    async handleResendOtp(type) {
      try {
        if (!this.isResendDisabled) {
          useUIStore().updateLoading({ isLoading: true, message: 'Resending OTP...' });
          let reqBody = {
            type: type
          };
          await apiInterceptor.post(environment('VUE_APP_API_URL') + "auth/mfa-setup/edit/request", reqBody);
          useUIStore().updateToast({ open: true, message: "OTP has been sent to your email", type: "success" });
          this.expiryTimer = 300;   // Reset the timer to 5 minutes
          this.isResendDisabled = true; // Disable resend until timer expires
          this.startResendTimer(); // Reset the resend OTP timer
          clearInterval(this.timerInterval);
          this.startTimer();
        }
			} catch (error) {
				console.error("Exception", error);
				useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong",
          type: "danger"
        })
			} finally {
				useUIStore().updateLoading({ isLoading: false });
			}
    },
    startTimer() {
      this.timerInterval = setInterval(() => {
        if (this.expiryTimer > 0) {
          this.expiryTimer--;
        } else {
          clearInterval(this.timerInterval);
          this.isResendDisabled = false; // Enable resend after timer expires
        }
      }, 1000);
    },
    async updateUserAccountDetails() {
    try {
      // Validate name/email
      if (this.roleAccountDetails.email === '' || this.roleAccountDetails.name === '') {
        useUIStore().updateToast({
          open: true,
          message: "Please enter valid Name/email and try again",
          type: "danger"
        });
        return;
      }

      useUIStore().updateLoading({ isLoading: true, message: 'Saving profile...' });
      const profileReqBody = {};
      let hasProfileChanges = false;

      // Check for profile changes (name, email, etc.)
      for (let prop in this.roleAccountDetails) {
        if (this.roleAccountDetails[prop] !== this.roleDetails[prop]) {
          profileReqBody[prop] = this.roleAccountDetails[prop];
          hasProfileChanges = true;
        }
      }

      // Check if password needs update
      const changingPassword = this.currPwd || this.newPwd || this.confirmPwd;

      if (changingPassword) {
        // Validate password rules
        if (!this.isNewPwValid) {
          useUIStore().updateToast({
            open: true,
            message: "Please fulfill the new password validations",
            type: "warning"
          });
          return;
        }

        if (!this.pwValidMatch) {
          useUIStore().updateToast({
            open: true,
            message: "The new password and confirm password do not match",
            type: "warning"
          });
          return;
        }

        if (this.currPwd === this.confirmPwd) {
          useUIStore().updateToast({
            open: true,
            message: "The new password cannot be the same as the old password",
            type: "warning"
          });
          return;
        }
      }

      if (!hasProfileChanges && !changingPassword) {
        useUIStore().updateToast({
          open: true,
          message: "No changes detected to update",
          type: "warning"
        });
        return;
      }

      // Update profile details if there are changes
      if (hasProfileChanges) {
        await apiInterceptor.patch(environment("VUE_APP_API_URL") + "roles/v1/role-details/", profileReqBody);
      }

      // Update password if needed
      if (changingPassword) {
        const refreshToken = useAuthStore().authData?.refreshToken;
        const passwordReqBody = {
          old_password: this.currPwd,
          password: this.newPwd,
          confirm_password: this.confirmPwd,
          refresh: refreshToken
        };
        await apiInterceptor.post(environment("VUE_APP_API_URL") + "auth/password/change", passwordReqBody);
      }

      const msg = changingPassword && hasProfileChanges 
        ? "Your account details and password have been updated successfully!" 
        : changingPassword 
          ? "Password updated successfully!"
          : "Account details saved successfully!";
      
      useUIStore().updateToast({ open: true, message: msg, type: "success" });
      
      // Only refresh role data if profile details were updated
      if (hasProfileChanges) {
        await queryClient.invalidateQueries({ queryKey: ['account-settings'] });
        await queryClient.invalidateQueries({ queryKey: ['role-details'] });
        await useAuthStore().checkToken();
      }
      this.currPwd = undefined;
      this.newPwd = undefined;
      this.confirmPwd = undefined;
    } catch (error) {
      useUIStore().updateToast({
        open: true,
        message: error.message || "Something went wrong",
        type: "danger"
      });
    } finally {
      useUIStore().updateLoading({ isLoading: false });
    }
    },
    startResendTimer() {
      this.resendTimer = 20; // 20 seconds
      this.isResendDisabled = true;
      this.resendInterval = setInterval(() => {
        if (this.resendTimer > 0) {
          this.resendTimer--;
        } else {
          this.isResendDisabled = false;
          clearInterval(this.resendInterval);
          this.resendInterval = null;
        }
      }, 1000);
    },
  }
}
</script>

<style lang="scss" scoped src="../../resources/css/settings.scss"> </style>
<style lang="scss" scoped>
.chat-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--white);
}
.toolbar-start {
  display: flex;
  align-items: center;
}
.vivid-input {
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
  width: 100%;
}
.passwordField {
  position: relative;
}
.passwordField .passwordToggle {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  cursor: pointer;
  z-index: 999;
}
.channelIcon {
  width: 40px;
  height: 40px;
}
.channelIcon img {
  width: 100%;
  height: 100%;
  border-radius: 5px;
}
.liveChatLinks {
  display: flex;
  justify-content: flex-start;
  gap: 20px;
}
.paddingTop {
  padding-top: 10px;
}
.footerBttn {
  display: flex;
  justify-content: center;
  gap: 20px;
  width: 100%;
}
</style>
